// Accounting render — renders a single period (acc_long or acc_shor)
// using the format strings from cur_recv.
//
// Ported from admin/index.php's inline <script> block (lines 1469-1712).
// The inline versions read `currentTerminal` from module scope; the bundle
// versions take `state: TimestopState` as the first argument.

import { getCurrencyByISO, formatCurrency } from './utils';
import type { TimestopState } from './state';
import type { Terminal } from './types';
import {formatDividedCurrency} from "./utils/currency";
import { t as i18n } from './i18n';

// Normalised accounting shape — the "user-friendly" form that the
// renderer expects. Money in has exactly two channels (the cabinet no
// longer takes cards) and the game counters replace the old per-hopper
// "token out" bucket.
//
//   { st, in: { coin: {ISO: v}, note: {ISO: v} },
//     games: { count: [g1,g2,g3], wins: [w1,w2,w3] } }
export interface NormalisedAcc {
  st: string;
  in: {
    coin: Record<string, number>;
    note: Record<string, number>;
  };
  token: {
    out: number;
    hand: number;
  };
  games: {
    count: number[];
    wins: number[];
  };
}

// Legacy snapshot shapes still present in older acc_diff rows: the
// Coinly-era BoOps raw shape (in_coins / in_notes / in_card /
// out_count / out_values1..3) and the intermediate {st,in,out} one.
interface RawAccBoOps {
  in_coins?: Record<string, number>;
  in_notes?: Record<string, number>;
  in_card?: Record<string, number>;
  out_values1?: Record<string, number>;
  out_values2?: Record<string, number>;
  out_values3?: Record<string, number>;
  out_count?: number[];
  start_date?: string;
  [k: string]: unknown;
}

// Token counters: `token.out` (dispensed) and `token.hand` (in the
// hoppers). Flat token_out / token_hand spellings are accepted too.
function normaliseToken(
  r: Record<string, unknown>,
  _games?: unknown,
): { out: number; hand: number } {
  const tok = (r.token && typeof r.token === 'object') ? r.token as Record<string, unknown> : {};
  const pick = (nested: string, flat: string): number => {
    if (Object.prototype.hasOwnProperty.call(tok, nested)) return Number(tok[nested] || 0);
    return Number((r[flat] as number | undefined) || 0);
  };
  return { out: pick('out', 'token_out'), hand: pick('hand', 'token_hand') };
}

// Three-slot counter array (games count / wins), zero-padded.
function normaliseSlots(v: unknown): number[] {
  const out = [0, 0, 0];
  if (Array.isArray(v)) {
    for (let i = 0; i < 3; i++) out[i] = Number(v[i] || 0);
  }
  return out;
}

// Normalise accounting data from any shape the DB may hold into the
// canonical one. Card payments and the per-hopper out bucket are
// dropped: they no longer exist on the cabinet, and rendering zeros for
// them would invent data.
export function normaliseAccounting(raw: unknown): NormalisedAcc | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;

  const inSection = r.in as
    | { coin?: Record<string, number>; note?: Record<string, number>; card?: Record<string, number> }
    | undefined;
  const gamesSection = r.games as { count?: unknown; wins?: unknown } | undefined;

  // Canonical (api/coin.php) or intermediate shape: money in + games.
  if (inSection || gamesSection) {
    return {
      st: String(r.st || r.start_date || ''),
      in: {
        coin: inSection?.coin || {},
        note: inSection?.note || {},
      },
      token: normaliseToken(r, gamesSection),
      games: {
        count: normaliseSlots(gamesSection?.count),
        wins: normaliseSlots(gamesSection?.wins),
      },
    };
  }

  // Legacy BoOps raw shape. Games never existed there, so they stay
  // zero; only money in carries over.
  const boOps = r as RawAccBoOps;
  return {
    st: boOps.start_date || '',
    in: {
      coin: boOps.in_coins || {},
      note: boOps.in_notes || {},
    },
    token: normaliseToken(r),
    games: { count: [0, 0, 0], wins: [0, 0, 0] },
  };
}

// Helper: pick the i-th value of each currency across a list of
// currency→value maps.
export function pickByIndex(
  maps: Array<Record<string, number> | undefined>,
  i: number,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const m of maps) {
    if (!m) continue;
    const vals = Object.values(m);
    const keys = Object.keys(m);
    for (let k = 0; k < keys.length; k++) {
      out[keys[k]] = vals[i] || 0;
    }
  }
  return out;
}

// Public entry point. Renders the accounting tab for the given period key
// ('acc_shor' or 'acc_long') into the supplied container element.
export function renderAccounting(
  state: TimestopState,
  container: HTMLElement,
  periodKey: string,
): void {
  // Wipe whatever was there.
  container.textContent = '';

  const periodLabel = periodKey === 'acc_long' ? i18n('tab_accounting.period_long') : i18n('tab_accounting.period_short');
  const ct = state.currentTerminal;
  if (!ct) return;
  const raw = ct[periodKey];
  const data = normaliseAccounting(raw);

  const wrapper = document.createElement('div');
  wrapper.className = 'space-y-4';

  if (!data) {
    // Even with no data, surface the period label so the user knows
    // which bucket they're looking at.
    wrapper.appendChild(makeAccHeader(periodLabel, null));
    const empty = document.createElement('div');
    empty.className = 'bg-gray-100 rounded-2xl p-4 text-gray-400 text-sm';
    empty.textContent = i18n('tab_accounting.no_data_terminal');
    wrapper.appendChild(empty);
    container.appendChild(wrapper);
    return;
  }

  // Header row: title + period start
  wrapper.appendChild(makeAccHeader(periodLabel, data.st));
  renderAccTable(wrapper, ct, data, state);
  container.appendChild(wrapper);
}

// makeAccHeader -- "<Title>" on the left, "since <st>" (or "no data")
// on the right. Shared by Short/Long (periodLabel + data.st) and
// Ranged ("Ranged" + date range).
export function makeAccHeader(title: string, st: string | null): HTMLDivElement {
  const header = document.createElement('div');
  header.className = 'flex items-baseline justify-between mx-3 sm:mx-0';
  const t = document.createElement('div');
  t.className = 'font-semibold text-emerald-700';
  t.textContent = title;
  header.appendChild(t);
  const r = document.createElement('div');
  r.className = 'text-xs text-gray-500 font-mono';
  r.textContent = st ? i18n('tab_accounting.since', { stamp: st }) : i18n('tab_accounting.no_data');
  header.appendChild(r);
  return header;
}

// renderAccTable -- shared table renderer used by both the per-period
// (Short/Long) view and the Ranged view. Ranged caller passes an
// already-summed NormalisedAcc; renderAccTable does NOT draw the
// "title + since" header (callers do that themselves via
// makeAccHeader so the Ranged tab can show a date range instead
// of "since ...").
export function renderAccTable(
  container: HTMLElement,
  ct: Terminal,
  data: NormalisedAcc,
  state?: TimestopState | null,
): void {
  // ---- Single shared table for IN + OUT -----------------------
  const isos = Object.keys(ct.currencyLookup || {});

  if (isos.length === 0) {
    const noCur = document.createElement('div');
    noCur.className = 'bg-gray-100 rounded-2xl p-4 text-gray-400 text-sm';
    noCur.textContent = i18n('tab_accounting.no_currencies');
    container.appendChild(noCur);
    return;
  }

  // Build a tailwind-style grid template: label / each currency.
  // There is no count column any more: money in is an amount per
  // channel, and the game counters have their own table below.
  const gridTemplate =
    'minmax(100px,1fr) ' + // Channel
    isos.map(() => 'minmax(0,1fr)').join(' ');

  const table = document.createElement('div');
  table.className = 'bg-white border rounded-2xl overflow-hidden text-sm';

  // ---- Header row ----
  const headRow = document.createElement('div');
  headRow.className =
    'grid items-center gap-x-2 px-4 py-2 bg-gray-100 ' +
    'text-xs uppercase tracking-widest text-gray-500 border-b';
  headRow.style.gridTemplateColumns = gridTemplate;

  const headLabel = document.createElement('div');
  headLabel.className = 'text-left font-medium';
  headLabel.textContent = i18n('tab_accounting.money_in');
  headRow.appendChild(headLabel);

  for (const iso of isos) {
    const th = document.createElement('div');
    th.className = 'text-right font-medium tabular-nums';
    th.textContent = iso;
    headRow.appendChild(th);
  }
  table.appendChild(headRow);

  const inChannels: Array<{ label: string; map: Record<string, number> }> = [
    { label: i18n('tab_accounting.ch_coin'), map: data.in.coin || {} },
    { label: i18n('tab_accounting.ch_note'), map: data.in.note || {} },
  ];
  for (const ch of inChannels) {
    const tr = document.createElement('div');
    tr.className =
      'grid items-center gap-x-2 px-4 py-1.5 border-b border-gray-100 last:border-b-0';
    tr.style.gridTemplateColumns = gridTemplate;

    const lbl = document.createElement('div');
    lbl.className = 'text-left text-gray-700';
    lbl.textContent = ch.label;
    tr.appendChild(lbl);

    for (const iso of isos) {
      const cell = document.createElement('div');
      const v = Number(ch.map[iso] || 0);
      cell.className =
        'text-right tabular-nums ' + (v === 0 ? 'text-gray-300' : 'text-gray-800');
      const cur = getCurrencyByISO(ct, iso, state);
      cell.textContent = v === 0 ? '0' : formatDividedCurrency(v, cur);
      tr.appendChild(cell);
    }
    table.appendChild(tr);
  }

  // ---- Sum row: coins + notes per currency ----
  const sumRow = document.createElement('div');
  sumRow.className =
    'grid items-center gap-x-2 px-4 py-2 bg-gray-50 border-t border-gray-200 font-semibold';
  sumRow.style.gridTemplateColumns = gridTemplate;

  const sumLbl = document.createElement('div');
  sumLbl.className = 'text-left text-gray-700';
  sumLbl.textContent = i18n('tab_accounting.sum');
  sumRow.appendChild(sumLbl);

  for (const iso of isos) {
    const v = Number((data.in.coin || {})[iso] || 0) + Number((data.in.note || {})[iso] || 0);
    const cell = document.createElement('div');
    cell.className = 'text-right tabular-nums ' + (v === 0 ? 'text-gray-400' : 'text-gray-900');
    const cur = getCurrencyByISO(ct, iso, state);
    cell.textContent = v === 0 ? '0' : formatDividedCurrency(v, cur);
    sumRow.appendChild(cell);
  }
  table.appendChild(sumRow);

  container.appendChild(table);

  // ---- Section: Tokens (out / in hand / sum) ----
  const tk = data.token || { out: 0, hand: 0 };
  const tkOut = Number(tk.out || 0);
  const tkHand = Number(tk.hand || 0);
  const tkCols = 'minmax(100px,1fr) minmax(70px,90px)';

  const tkTable = document.createElement('div');
  tkTable.className = 'bg-white border rounded-2xl overflow-hidden text-sm mt-4';

  const tkHead = document.createElement('div');
  tkHead.className =
    'grid items-center gap-x-2 px-4 py-2 bg-gray-100 ' +
    'text-xs uppercase tracking-widest text-gray-500 border-b';
  tkHead.style.gridTemplateColumns = tkCols;
  const tkHeadLbl = document.createElement('div');
  tkHeadLbl.className = 'text-left font-medium';
  tkHeadLbl.textContent = i18n('tab_accounting.tokens');
  tkHead.appendChild(tkHeadLbl);
  const tkHeadVal = document.createElement('div');
  tkHeadVal.className = 'text-right font-medium';
  tkHeadVal.textContent = '';
  tkHead.appendChild(tkHeadVal);
  tkTable.appendChild(tkHead);

  const tokenRow = (label: string, value: number, emphasise: boolean): void => {
    const tr = document.createElement('div');
    tr.className =
      'grid items-center gap-x-2 px-4 py-1.5 border-b border-gray-100 last:border-b-0' +
      (emphasise ? ' bg-gray-50 font-semibold' : '');
    tr.style.gridTemplateColumns = tkCols;

    const lbl = document.createElement('div');
    lbl.className = 'text-left text-gray-700';
    lbl.textContent = label;
    tr.appendChild(lbl);

    const cell = document.createElement('div');
    cell.className = 'text-right tabular-nums ' +
      (value === 0 ? 'text-gray-300' : (emphasise ? 'text-gray-900' : 'text-gray-800'));
    cell.textContent = value.toLocaleString();
    tr.appendChild(cell);

    tkTable.appendChild(tr);
  };
  tokenRow(i18n('tab_accounting.token_out_row'), tkOut, false);
  tokenRow(i18n('tab_accounting.token_hand_row'), tkHand, false);
  tokenRow(i18n('tab_accounting.sum'), tkOut + tkHand, true);
  container.appendChild(tkTable);

  // ---- Section: Games (3 slots: plays + wins) ----
  // The cabinet reports three game counters instead of the old per-hopper
  // token bucket: games_count1..3 (games played) and games_win_count1..3
  // (games won). Count and wins sit side by side, one row per game type.
  const games = data.games || { count: [0, 0, 0], wins: [0, 0, 0] };
  const gCols = 'minmax(100px,1fr) minmax(70px,90px) minmax(70px,90px)';

  const gamesTable = document.createElement('div');
  gamesTable.className = 'bg-white border rounded-2xl overflow-hidden text-sm mt-4';

  const gHead = document.createElement('div');
  gHead.className =
    'grid items-center gap-x-2 px-4 py-2 bg-gray-100 ' +
    'text-xs uppercase tracking-widest text-gray-500 border-b';
  gHead.style.gridTemplateColumns = gCols;
  const gHeadCells: Array<[string, string]> = [
    [i18n('tab_accounting.bets'), 'text-left font-medium'],
    [i18n('tab_accounting.col_games'), 'text-right font-medium'],
    [i18n('tab_accounting.col_wins'), 'text-right font-medium'],
  ];
  for (const [label, cls] of gHeadCells) {
    const c = document.createElement('div');
    c.className = cls;
    c.textContent = label;
    gHead.appendChild(c);
  }
  gamesTable.appendChild(gHead);

  for (let i = 0; i < 3; i++) {
    const played = Number(games.count[i] || 0);
    const won = Number(games.wins[i] || 0);

    const tr = document.createElement('div');
    tr.className = 'grid items-center gap-x-2 px-4 py-1.5 border-b border-gray-100 last:border-b-0';
    tr.style.gridTemplateColumns = gCols;

    const lbl = document.createElement('div');
    lbl.className = 'text-left text-gray-700';
    lbl.textContent = i18n('tab_accounting.bet_n', { n: i + 1 });
    tr.appendChild(lbl);

    const cPlayed = document.createElement('div');
    cPlayed.className = 'text-right tabular-nums ' + (played === 0 ? 'text-gray-300' : 'text-gray-800');
    cPlayed.textContent = String(played);
    tr.appendChild(cPlayed);

    const cWon = document.createElement('div');
    cWon.className = 'text-right tabular-nums ' +
      (won === 0 ? 'text-gray-300' : 'text-emerald-700 font-semibold');
    cWon.textContent = String(won);
    tr.appendChild(cWon);

    gamesTable.appendChild(tr);
  }

  // Sum row: totals across the three bets.
  const gSum = document.createElement('div');
  gSum.className =
    'grid items-center gap-x-2 px-4 py-2 bg-gray-50 border-t border-gray-200 font-semibold';
  gSum.style.gridTemplateColumns = gCols;

  const gSumLbl = document.createElement('div');
  gSumLbl.className = 'text-left text-gray-700';
  gSumLbl.textContent = i18n('tab_accounting.sum');
  gSum.appendChild(gSumLbl);

  const gSumPlayed = document.createElement('div');
  const playedTotal = games.count.reduce((a: number, b: unknown) => a + Number(b || 0), 0);
  gSumPlayed.className = 'text-right tabular-nums ' + (playedTotal === 0 ? 'text-gray-400' : 'text-gray-900');
  gSumPlayed.textContent = playedTotal.toLocaleString();
  gSum.appendChild(gSumPlayed);

  const gSumWon = document.createElement('div');
  const wonTotal = games.wins.reduce((a: number, b: unknown) => a + Number(b || 0), 0);
  gSumWon.className = 'text-right tabular-nums ' + (wonTotal === 0 ? 'text-gray-400' : 'text-emerald-800');
  gSumWon.textContent = wonTotal.toLocaleString();
  gSum.appendChild(gSumWon);

  gamesTable.appendChild(gSum);
  container.appendChild(gamesTable);
}
